type StorageLike = Pick<Storage, "getItem" | "setItem" | "removeItem">;

export const PURCHASE_IDEMPOTENCY_STORAGE_KEY =
  "reputech_purchase_idempotency_v1";
export const CART_SHARE_STORAGE_KEY = "cart-share-uuid";
/** A purchase attempt never outlives the fixed 24h app-session window. */
export const PURCHASE_IDEMPOTENCY_TTL_MS = 24 * 60 * 60 * 1000;

export type PurchaseIdempotencyRecord = {
  idempotency_key: string;
  transaction_id: string;
  cart_uuid: string | null;
  created_at: number;
};

const getDefaultStorage = (): StorageLike | null => {
  if (typeof window === "undefined") return null;

  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

/**
 * The persistent-cart UUID lives in **localStorage** (written by
 * usePersistentCartInit so it survives tabs and restarts). sessionStorage is
 * only a fallback for browsers that block localStorage but allow session
 * storage, mirroring CompositeStorage's attribution strategy.
 */
export const getDefaultCartUuidStorages = (): StorageLike[] => {
  if (typeof window === "undefined") return [];

  const storages: StorageLike[] = [];
  // Lazy getters: the property access itself (`window.localStorage`) is what
  // throws in browsers that block storage, so it must happen inside each
  // candidate's own try block — an eager array literal would abort before the
  // sessionStorage fallback is ever probed.
  for (const readCandidate of [
    (): Storage => window.localStorage,
    (): Storage => window.sessionStorage,
  ]) {
    try {
      const candidate = readCandidate();
      void candidate.getItem;
      storages.push(candidate);
    } catch {
      // Skip this storage.
    }
  }
  return storages;
};

const safeJsonParse = <T>(value: string | null): T | null => {
  if (!value) return null;

  try {
    return JSON.parse(value) as T;
  } catch {
    return null;
  }
};

/**
 * Reads the persistent-cart UUID that anchors one checkout attempt. The backend
 * rotates it after a successful order, so a new purchase in the same tab gets a
 * fresh key while reloads and payment returns keep the previous one.
 *
 * Reads localStorage first (where usePersistentCartInit writes it) and falls
 * back to sessionStorage. Pass `storage` explicitly in tests; production uses
 * `getDefaultCartUuidStorages()`.
 */
export const readCartShareUuid = (
  storage?: StorageLike | null,
): string | null => {
  const storages =
    storage === undefined ? getDefaultCartUuidStorages() : [storage];

  for (const storageLike of storages) {
    if (!storageLike) continue;

    let rawValue: string | null = null;

    try {
      rawValue = storageLike.getItem(CART_SHARE_STORAGE_KEY);
    } catch {
      continue;
    }

    const parsed = safeJsonParse<{ uuid?: unknown }>(rawValue);
    const uuid = parsed?.uuid;

    if (typeof uuid === "string" && uuid !== "") {
      return uuid;
    }
  }

  return null;
};

const validatePurchaseIdempotencyRecord = (
  parsed: PurchaseIdempotencyRecord | null,
  now: number,
): PurchaseIdempotencyRecord | null => {
  if (
    !parsed ||
    typeof parsed.idempotency_key !== "string" ||
    parsed.idempotency_key === "" ||
    typeof parsed.transaction_id !== "string" ||
    parsed.transaction_id === "" ||
    typeof parsed.created_at !== "number"
  ) {
    return null;
  }

  if (
    now - parsed.created_at < 0 ||
    now - parsed.created_at >= PURCHASE_IDEMPOTENCY_TTL_MS
  ) {
    return null;
  }

  return parsed;
};

export const getStoredPurchaseIdempotencyRecord = (
  now: number,
  storage: StorageLike | null = getDefaultStorage(),
): PurchaseIdempotencyRecord | null => {
  if (!storage) return null;

  let rawValue: string | null = null;

  try {
    rawValue = storage.getItem(PURCHASE_IDEMPOTENCY_STORAGE_KEY);
  } catch {
    return null;
  }

  return validatePurchaseIdempotencyRecord(
    safeJsonParse<PurchaseIdempotencyRecord>(rawValue),
    now,
  );
};

/**
 * Page-lifetime mirror of the sticky record. Browsers that block **both**
 * localStorage and sessionStorage would otherwise mint a fresh random id on
 * every single call, so repeated calls inside one checkout attempt could no
 * longer collapse onto one key/transaction id. All callers run in client-side
 * event/order handlers, so this module state is page-scoped like
 * `orderTrackingDedupe`'s in-memory set.
 */
let inMemoryIdempotencyRecord: PurchaseIdempotencyRecord | null = null;

/** Test isolation only; production never resets the mirror. */
export const resetInMemoryIdempotencyForTesting = (): void => {
  inMemoryIdempotencyRecord = null;
};

type ActiveIdempotencyRecord =
  | { record: PurchaseIdempotencyRecord; fromStorage: true }
  | { record: PurchaseIdempotencyRecord | null; fromStorage: false };

/**
 * Storage first, then the page-lifetime mirror: a blocked or evicted storage
 * must not change the answer an earlier call in this page already produced.
 */
const readActiveIdempotencyRecord = (
  now: number,
  recordStorage: StorageLike | null,
): ActiveIdempotencyRecord => {
  const stored = getStoredPurchaseIdempotencyRecord(now, recordStorage);

  if (stored) return { record: stored, fromStorage: true };

  return {
    record: validatePurchaseIdempotencyRecord(
      inMemoryIdempotencyRecord,
      now,
    ),
    fromStorage: false,
  };
};

export const clearPurchaseIdempotency = (
  storage: StorageLike | null = getDefaultStorage(),
): void => {
  // Storage-blocked browsers keep the anchor only here, so the mirror has to
  // go too or the cleared checkout would resurrect its old key.
  inMemoryIdempotencyRecord = null;

  if (!storage) return;

  try {
    storage.removeItem(PURCHASE_IDEMPOTENCY_STORAGE_KEY);
  } catch {
    // Clearing is best-effort.
  }
};

export const createPurchaseIdempotencyRandomId = (): string => {
  const cryptoObject =
    typeof globalThis.crypto === "undefined"
      ? undefined
      : (globalThis.crypto as Crypto);

  if (typeof cryptoObject?.randomUUID === "function") {
    return cryptoObject.randomUUID();
  }

  return `${Date.now().toString(36)}-${Math.random()
    .toString(36)
    .substring(2, 10)}`;
};

const persistRecord = (
  record: PurchaseIdempotencyRecord,
  storage: StorageLike | null,
): void => {
  // The mirror is written unconditionally: it is the only anchor left when
  // storage is blocked, and it must never lag behind a successful write.
  inMemoryIdempotencyRecord = record;

  if (!storage) return;

  try {
    storage.setItem(PURCHASE_IDEMPOTENCY_STORAGE_KEY, JSON.stringify(record));
  } catch {
    // Storage-blocked browsers keep using the in-flight value for this page.
  }
};

/**
 * Returns the sticky idempotency key for the current checkout attempt.
 *
 * Reloads, payment-provider returns and double submissions of the same attempt
 * resolve to the same key; a rotated persistent-cart UUID (i.e. an order that
 * already completed) starts a new key. Callers send this value as
 * `idempotency_key` on the order-create payload so the backend can collapse
 * duplicates instead of creating two orders.
 */
export const getPurchaseIdempotencyKey = ({
  cartUuid,
  storage,
  now = Date.now(),
  createRandomId = createPurchaseIdempotencyRandomId,
}: {
  /**
   * Defaults to the persistent-cart UUID, read from localStorage with a
   * sessionStorage fallback (`readCartShareUuid`).
   */
  cartUuid?: string | null;
  /** Defaults to sessionStorage for the sticky record itself. */
  storage?: StorageLike | null;
  now?: number;
  createRandomId?: () => string;
} = {}): string => {
  const recordStorage = storage === undefined ? getDefaultStorage() : storage;
  const effectiveCartUuid =
    cartUuid === undefined ? readCartShareUuid(storage) : cartUuid;
  const active = readActiveIdempotencyRecord(now, recordStorage);
  const activeMatchesCart =
    !!active.record &&
    (active.record.cart_uuid ?? null) === (effectiveCartUuid ?? null);

  if (active.record && activeMatchesCart) {
    if (!active.fromStorage) {
      // The answer came from the mirror (blocked or evicted storage): heal the
      // persistent copy so a later reload finds the same attempt again.
      persistRecord(active.record, recordStorage);
    }
    return active.record.idempotency_key;
  }

  const idempotencyKey = effectiveCartUuid
    ? `cart_${effectiveCartUuid}`
    : `checkout_${createRandomId()}`;

  persistRecord(
    {
      idempotency_key: idempotencyKey,
      // Seed the transaction id from the same anchor so both stay consistent
      // within one attempt even when the order reference is missing.
      transaction_id: createTransactionIdFromKey(idempotencyKey),
      cart_uuid: effectiveCartUuid ?? null,
      created_at: now,
    },
    recordStorage,
  );

  return idempotencyKey;
};

const createTransactionIdFromKey = (idempotencyKey: string): string =>
  `T_${idempotencyKey}`;

/**
 * Stable fallback transaction id for purchase events emitted before (or
 * without) a backend order reference. Repeated calls inside one checkout
 * attempt return the same id, so reloads and payment returns cannot produce a
 * second GA4/Meta/Bing purchase event with a different transaction id.
 */
export const getStickyFallbackTransactionId = ({
  cartUuid,
  storage,
  now = Date.now(),
  createRandomId = createPurchaseIdempotencyRandomId,
}: {
  /**
   * Defaults to the persistent-cart UUID, read from localStorage with a
   * sessionStorage fallback (`readCartShareUuid`).
   */
  cartUuid?: string | null;
  /** Defaults to sessionStorage for the sticky record itself. */
  storage?: StorageLike | null;
  now?: number;
  createRandomId?: () => string;
} = {}): string => {
  getPurchaseIdempotencyKey({ cartUuid, storage, now, createRandomId });

  const recordStorage = storage === undefined ? getDefaultStorage() : storage;
  const active = readActiveIdempotencyRecord(now, recordStorage);

  return (
    active.record?.transaction_id ??
    createTransactionIdFromKey(`orphan_${createRandomId()}`)
  );
};
